// src/components/Esim/PaymentStep/PaymentStep.js
import React, { useEffect, useMemo, useState } from "react";
import "./payment-step.scss";
import Navbar from "../../Navbar/Navbar";
import EsimPlanDetails from "../EsimDetail/EsimPlanDetails";
import ApplyPromoCode from "../../ui/commons/ApplyPromoCode";
import { AppError, UnauthorizedError } from "../../../utils/customError";
import ErrorModal from "../../../components/ui/commons/ErrorModal";
import { FloatButton } from "antd";
import KycDialog from "../../../components/ui/commons/KycDialog";
import LoginModal from "../../../components/Auth/LoginModal";
import { CalendarDays, CreditCard, Globe2, ShieldCheck, Sparkles, Tag, Wifi } from "lucide-react";

import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { post } from "../../../utils/request";
import { getTimezone } from "../../../utils/context";

/* ------------------------------ Helpers ------------------------------ */
const formatGbFromBytes = (bytes, decimals = 2) => {
  const num = typeof bytes === "number" ? bytes : Number(bytes);
  if (!isFinite(num) || isNaN(num)) return "0.00";
  return (num / (1024 * 1024 * 1024)).toFixed(decimals);
};

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

/* -------------------------------- View ------------------------------ */
const PaymentStep = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    selectedPackage,
    esimPreorderResult,
    locationType,
    location: locationValue,
  } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [preorderResult, setPreorderResult] = useState(
    esimPreorderResult || null
  );

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [showKyc, setShowKyc] = useState(false);
  const [kycPrompt, setKycPrompt] = useState(null);
  const [orderId, setOrderId] = useState(null);

  const [openLoginModal, setOpenLoginModal] = useState(false);

  /* ---- Volume (Bytes → GB) ---- */
  const bytesVolume = useMemo(
    () => (selectedPackage?.volume ? Number(selectedPackage.volume) : 0),
    [selectedPackage?.volume]
  );
  const displayGB = useMemo(
    () => formatGbFromBytes(bytesVolume),
    [bytesVolume]
  );

  const currency =
    selectedPackage?.currencyCode ||
    preorderResult?.priceInfo?.price?.currency ||
    "USD";
  const basePrice = selectedPackage?.price ?? 0;

  useEffect(() => {
    loadRazorpayScript();
    setPreorderResult(esimPreorderResult || null);
  }, [esimPreorderResult]);

  /* ---- Payment ---- */
  const paymentNotify = async (paymentId) => {
    try {
      if (!paymentId) return;
      await post(`/payment/${paymentId}/notify`);
    } catch {
      // non-blocking
    }
  };

  const handlePayment = async () => {
    if (loading) return;

    if (!selectedPackage) {
      toast.error("No eSim package selected. Please choose a plan again.");
      navigate("/esim", { replace: true });
      return;
    }
    if (!preorderResult?.preorderInfo?.preorderId) {
      toast.error("Something Went Wrong. Please try again.");
      navigate("/esim", { replace: true });
      return;
    }

    setLoading(true);
    try {
      const timezone = getTimezone();
      const { orderId, serviceType, skipPayment } = await post(
        "/esim/submitOrder",
        {
          ...selectedPackage,
          preorderId: preorderResult.preorderInfo.preorderId,
          timezone,
        }
      );

      if (!orderId) {
        toast.error("Something went wrong, please try again.");
        return;
      }
      setOrderId(orderId);

      const paymentResult = await post("/payment/pay", {
        orderId: orderId,
        serviceType: "esim",
      });

      if (paymentResult.isHit) {
        hitRule(paymentResult.riskResult);
        return;
      }

      const payResult = paymentResult?.payResult;
      if (!payResult) {
        toast.error("Something went wrong, please try again.");
        return;
      }

      if (payResult.skipPayment === true) {
        navigate(`/my-bookings/esim/${orderId}`, { replace: true });
        return;
      }

      const options = {
        key: payResult.appkey,
        order_id: payResult.providerOrderId,
        amount: payResult.amount,
        currency: payResult.currency,
        name: payResult.name,
        description: payResult.description,
        timeout: payResult.timeout,
        handler: async () => {
          await paymentNotify(payResult.paymentId);
          setLoading(false);
          navigate(`/my-bookings/esim/${orderId}`, { replace: true });
        },
        prefill: {
          name: payResult.fullName,
          email: payResult.email,
          contact: payResult.contact,
        },
        theme: { color: "#3399cc" },
        // callback_url: `/my-bookings/esim/${orderId}`,
        // redirect: true
        modal: {
          ondismiss: function () {
            console.log("Checkout form closed");
            navigate(`/my-bookings/esim/${orderId}`, { replace: true });
          },
        },
      };

      if (!window.Razorpay) {
        const loaded = await loadRazorpayScript();
        if (!loaded) {
          toast.error("Payment system failed to load. Please try again.");
          return;
        }
      }

      var rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        toast.error("Payment failed");
        navigate(`/my-bookings/esim/${orderId}`, { replace: true });
      });

      rzp.open();
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setOpenLoginModal(true);
        return;
      }
      if (err instanceof AppError && err.code === 20001) {
        showErrorInfo(err.message);
        return;
      }

      console.error("Payment Error:", err);
      toast.error("Something went wrong, please try again.");
    } finally {
      setLoading(false);
    }
  };

  function hitRule(riskResult) {
    const actionType = riskResult.action.type;
    if ("redirect_to_kyc" === actionType) {
      performKYC(riskResult);
    } else if ("block_checkout" === actionType) {
      //TODO block checkout
    }
  }

  function performKYC(riskResult) {
    const message = !!riskResult.isKycSubmitted
      ? riskResult.kycSubmittedPromptMessage
      : riskResult.message;

    setKycPrompt(message);
    setShowKyc(true);
  }

  /* ---- Promo ---- */
  const onApply = async (promoCode) => {
    try {
      const current = preorderResult?.preorderInfo
        ? { ...preorderResult.preorderInfo }
        : { ...(esimPreorderResult?.preorderInfo || {}) };

      current.promoCode = promoCode;

      const newPreorder = await post("/esim/preorder", current);

      if (!newPreorder || (!!promoCode && newPreorder?.promoInfo?.hasError)) {
        toast.error(
          newPreorder?.promoInfo?.errorMessage || "Invalid promo code."
        );
        return;
      }

      setPreorderResult(newPreorder);
      toast.success(promoCode ? "Promo applied!" : "Promo cleared.");
    } catch (err) {
      if (err instanceof AppError && err.code === 20001) {
        showErrorInfo(err.message);
        return;
      }

      toast.error("Failed to apply promo code. Please try again.");
    }
  };

  function showErrorInfo(errorMessage) {
    setErrorMessage(errorMessage);
    setShowError(true);
  }

  function onErrorConfirmed() {
    setShowError(false);
    navigate(`/esim?locationType=${locationType}&location=${locationValue}`, {
      replace: true,
    });
  }

  const promoCode = preorderResult?.promoInfo?.promoUsage?.promoCode || "";
  const promoDeductionAmount =
    preorderResult?.priceInfo?.price?.promoDeductionAmount ?? 0;
  const promoCurrency = preorderResult?.priceInfo?.price?.currency || currency;
  const displayData = selectedPackage?.displayVolume || `${displayGB} GB`;
  const displayValidity =
    selectedPackage?.displayValidity ||
    `${selectedPackage?.duration || 0} Days`;
  const displayCoverage =
    selectedPackage?.displayCoverage ||
    selectedPackage?.planName ||
    locationValue ||
    "Selected destination";
  const totalPrice = preorderResult?.priceInfo?.totalPrice?.content || "";

  return (
    <>
      <Navbar />

      <section className="payment-step-section">
        <div className="payment-step-wrapper">
          {!selectedPackage ? (
            <div className="payment-step-empty">
              <p className="payment-step-eyebrow">Checkout session</p>
              <h1>Choose an eSIM plan to continue.</h1>
              <p>
                Your payment page needs a selected plan from the eSIM results page.
                Pick a destination and plan again to create a fresh checkout.
              </p>
              <button type="button" onClick={() => navigate("/esim")}>
                Browse eSIM plans
              </button>
            </div>
          ) : (
          <>
          <div className="payment-step-hero">
            <div>
              <p className="payment-step-eyebrow">Secure checkout</p>
              <h1>Review your eSIM before payment.</h1>
              <p>
                Confirm your data plan, apply a promo code, and complete payment
                securely. Your order details stay tied to this checkout session.
              </p>
            </div>
            <div className="payment-step-hero__total">
              <span>Total</span>
              <strong>{totalPrice || selectedPackage?.displayPrice || `${currency} ${basePrice}`}</strong>
            </div>
          </div>

          <div className="payment-step-content">
            <div className="left-side">
              <div className="checkout-plan-card">
                <div className="checkout-plan-card__header">
                  <div>
                    <p>Selected eSIM</p>
                    <h2>{selectedPackage?.planName || "Travel eSIM plan"}</h2>
                  </div>
                  <span>{selectedPackage?.displayPrice || `${currency} ${basePrice}`}</span>
                </div>
                <div className="checkout-plan-card__metrics">
                  <div>
                    <Wifi size={18} />
                    <span>Data</span>
                    <strong>{displayData}</strong>
                  </div>
                  <div>
                    <CalendarDays size={18} />
                    <span>Validity</span>
                    <strong>{displayValidity}</strong>
                  </div>
                  <div>
                    <Globe2 size={18} />
                    <span>Coverage</span>
                    <strong>{displayCoverage}</strong>
                  </div>
                </div>
                <div className="checkout-plan-card__note">
                  <ShieldCheck size={18} />
                  <span>Plan details are checked again before the final order is created.</span>
                </div>
              </div>

              <EsimPlanDetails selectedPackage={selectedPackage} showPlanOverview={false} />
            </div>

            <aside className="right-side checkout-sidebar">
              <div className="checkout-card checkout-card--promo">
                <div className="checkout-card__title">
                  <Tag size={18} />
                  <span>Promo code</span>
                </div>
                <ApplyPromoCode
                  promoCode={promoCode}
                  onApply={onApply}
                  type="outline"
                />
              </div>

              <div className="checkout-card summary-box">
                <div className="checkout-card__title">
                  <CreditCard size={18} />
                  <span>Order summary</span>
                </div>

                <div className="summary-row">
                  <span>eSIM plan</span>
                  <strong>{selectedPackage?.planName}</strong>
                </div>

                <div className="summary-row">
                  <span>Data</span>
                  <strong>{displayData}</strong>
                </div>

                <div className="summary-row">
                  <span>Validity</span>
                  <strong>{displayValidity}</strong>
                </div>

                <div className="summary-divider" />

                {(preorderResult?.priceInfo?.priceDetails || []).map(
                  (priceDetail, index) => (
                    <div className="summary-row" key={`${priceDetail.title}-${index}`}>
                      <span>{priceDetail.title}</span>
                      <strong>{priceDetail.content}</strong>
                    </div>
                  )
                )}

                {promoDeductionAmount > 0 && (
                  <div className="summary-row summary-row--saving">
                    <span>Promo saving</span>
                    <strong>-{promoCurrency} {promoDeductionAmount}</strong>
                  </div>
                )}

                <div className="summary-row total">
                  <span>{preorderResult?.priceInfo?.totalPrice?.title || "Total"}</span>
                  <strong>{totalPrice}</strong>
                </div>
              </div>

              <div className="checkout-card policy-box">
                <strong>Refund Policy</strong>
                <p>
                  Unable to install your eSIM? No stress. Contact our support
                  team and we will help you review the order.
                </p>
              </div>

              <div className="checkout-card policy-box">
                <strong>Telly Credits</strong>
                <p>
                  Download Teleport Now to unlock member perks, Telly Credits,
                  referral rewards, and app-only travel offers.
                </p>
              </div>

              <button
                className="final-btn"
                onClick={handlePayment}
                disabled={loading}
              >
                {loading ? "Processing..." : "Proceed to Payment"}
              </button>

              <div className="checkout-security-row">
                <Sparkles size={15} />
                <span>Secure Payment powered by RazorPay</span>
              </div>
            </aside>
          </div>
          </>
          )}
        </div>

        <ErrorModal
          open={showError}
          message={errorMessage}
          onConfirmed={onErrorConfirmed}
        />

        <KycDialog
          isOpen={showKyc}
          onOk={() => {
            setShowKyc(false);
            navigate(`/my-bookings/esim/${orderId}`, { replace: true });
          }}
          onCancel={() => {
            setShowKyc(false);
            navigate(`/my-bookings/esim/${orderId}`, { replace: true });
          }}
          prompt={kycPrompt}
        />

        <FloatButton.BackTop
          shape="square"
          type="primary"
          style={{ marginRight: 20 }}
        />
      </section>

      <LoginModal
        isOpen={openLoginModal}
        onClose={() => setOpenLoginModal(false)}
      />
    </>
  );
};

export default PaymentStep;
